<template>
  <!-- ERROR -->
  <section v-if="error">
    <br />

    <p class="error">
      {{ error }}
    </p>
  </section>

  <!-- PAGE -->
  <section
    v-if="!error && loaded"
    class="page-container grid-container"
  >
    <div class="grid-container wizard">

      <!-- BACK -->
      <section>
        <div class="grid-x grid-margin-x">
          <div class="cell small-12">
            <a @click="router.back()">
              ← Back
            </a>
          </div>
        </div>
      </section>

      <!-- EDITABLE VERSION -->
      <section
        v-if="
          task &&
          task.taskStatus !== TASK_STATUS.SUCCESSFULLY_COMPLETED.value
        "
      >
        <div class="grid-x grid-margin-x">
          <div class="cell small-12">
            <h1 class="text-2xl font-bold">
              Payroll Run Details
            </h1>

            <p>
              To allow us to accurately deliver your payroll,
              please provide us with the details of your payroll
              runs. If you have multiple pay frequencies or pay
              dates, please provide us with the details of each.
            </p>

            <EditableTable
              v-model="payrollRunResponse"
              :columns="columns"
              buttonName="Add Payroll Run"
            />
          </div>
        </div>

        <br />

        <button
          v-if="payrollRunResponse.length > 0"
          type="button"
          class="btn btn-primary"
          @click="savePayrollRun"
        >
          Finalise Payroll Runs
        </button>
      </section>

      <!-- COMPLETED / READ-ONLY VERSION -->
      <section v-else>
        <div class="grid-x grid-margin-x">
          <div class="cell small-12">
            <h1 class="text-2xl font-bold">
              Payroll Runs
            </h1>

            <p>
              Thank you for providing your payroll run details.
              This information is currently being reviewed by
              our team.
            </p>

            <table
              v-if="
                payrollRunResponse &&
                payrollRunResponse.length
              "
              class="payroll-table"
            >
              <thead>
                <tr>
                  <th>Frequency</th>
                  <th>Pay Date</th>
                  <th>Pay Period Start</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="(payrollRun, index) in payrollRunResponse"
                  :key="index"
                >
                  <td>
                    {{ payrollRun.payFrequency }}
                  </td>

                  <td>
                    {{ formatDate(payrollRun.payDate) }}
                  </td>

                  <td>
                    {{ formatDate(payrollRun.payPeriodStart) }}
                  </td>
                </tr>
              </tbody>
            </table>

            <p v-else>
              No payroll runs have been provided.
            </p>

            <button
              type="button"
              class="btn btn-secondary"
              @click="router.back()"
            >
              Return to Tasks
            </button>
          </div>
        </div>
      </section>

    </div>
  </section>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import EditableTable from '@/components/controls/EditableTable.vue'

import {
  putDataCollectionTask,
  getDataCollectionTask
} from '@/services/implementationmanagement.api'

import { TASK_STATUS } from '@/helpers/constants'

const router = useRouter()

const loaded = ref(false)
const error = ref(null)
const task = ref(null)

/*
 * Always keep this as an array.
 *
 * EditableTable expects its modelValue to be an array.
 */
const payrollRunResponse = ref([])

const taskId =
  router.currentRoute.value.params.taskId

const errorString =
  'An error has occurred. Please contact your Qtac account manager.'

/*
 * Payroll frequency options.
 */
const payrollFrequencyOptions = [
  {
    value: 'Monthly',
    label: 'Monthly'
  },
  {
    value: 'Weekly',
    label: 'Weekly'
  },
  {
    value: 'Two Weekly',
    label: 'Two Weekly'
  },
  {
    value: 'Four Weekly',
    label: 'Four Weekly'
  },
  {
    value: 'Quarterly',
    label: 'Quarterly'
  },
  {
    value: 'Half Yearly',
    label: 'Half Yearly'
  },
  {
    value: 'Annual',
    label: 'Annual'
  }
]

/*
 * EditableTable column configuration.
 */
const columns = [
  {
    field: 'payFrequency',
    label: 'Frequency',
    type: 'combo',
    placeholder: 'Select frequency',
    options: payrollFrequencyOptions
  },

  {
    field: 'payDate',
    label: 'Pay Date',
    type: 'date',
    placeholder: 'Pay Date'
  },

  {
    field: 'payPeriodStart',
    label: 'Pay Period Start',
    type: 'date',
    placeholder: 'Pay Period Start'
  }
]

/*
 * Load the task when the page opens.
 */
onMounted(async () => {
  try {
    const response =
      await getDataCollectionTask(taskId)

    if (
      response.status !== 200 ||
      response.data === null ||
      response.data === undefined
    ) {
      error.value = errorString
      return
    }

    task.value = response.data

    /*
     * No existing JSON means this is a new response.
     */
    if (
      !task.value.dataJson ||
      task.value.dataJson.trim() === ''
    ) {
      payrollRunResponse.value = []
      loaded.value = true
      return
    }

    /*
     * Load previously saved payroll runs.
     */
    const parsedData =
      JSON.parse(task.value.dataJson)

    /*
     * Ensure the data is actually an array before
     * handing it to EditableTable.
     */
    payrollRunResponse.value =
      Array.isArray(parsedData)
        ? parsedData
        : []

    loaded.value = true
  } catch (e) {
    console.error(
      'Failed to load payroll run details:',
      e
    )

    error.value =
      'Failed to load payroll run data.'
  }
})

/*
 * Save and complete the task.
 */
const savePayrollRun = async () => {
  try {
    const payload = {
      version: 1,

      dataJson: JSON.stringify(
        payrollRunResponse.value
      ),

      schemaName: 'PayrollRunDetails',

      taskId,

      taskStatus: 'SuccessfullyCompleted'
    }

    const response =
      await putDataCollectionTask(
        taskId,
        payload
      )

    if (response.status !== 200) {
      error.value =
        'Error saving data. Please contact your Qtac account manager.'

      return false
    }

    router.back()

    return true
  } catch (e) {
    console.error(
      'Failed to save payroll run details:',
      e
    )

    error.value =
      'Error saving data. Please contact your Qtac account manager.'

    return false
  }
}

/*
 * Display ISO date:
 *
 * 2026-08-18
 *
 * as:
 *
 * 18/08/2026
 *
 * Avoid using new Date() here because date-only
 * ISO values can be affected by timezone conversion.
 */
const formatDate = (value) => {
  if (!value) {
    return ''
  }

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})/
  )

  if (!match) {
    return value
  }

  const [, year, month, day] = match

  return `${day}/${month}/${year}`
}
</script>

<style scoped>
.payroll-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  margin-bottom: 30px;
}

.payroll-table th,
.payroll-table td {
  padding: 20px;
  text-align: left;
}

.payroll-table th {
  font-weight: bold;
}
</style>